import React, { useEffect, useState } from 'react';
import { EditProjectContainer, SendProjectHeader } from './styles';
import CancelUploadFiles from './CancelUploadFilesModal/indexFacelift';
import { IFabricationProject } from '../../../../../../../store/ducks/fabrication-projects/types';
import { ApplicationState } from '../../../../../../../store/index';
import { useDispatch, useSelector } from 'react-redux';
import { loadMockups } from '../../../../../../../store/ducks/fabrication-mockups/actions';
import EditProjectForm, {
  IEditProjectForm
} from '../../../EditProjectForm/Facelift';
import { FabricationProjectsService } from '../../../../../../../services/FabricationProjects';
import { Modal } from '@gerdau/hefesto';
import { addToast } from '../../../../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../../../../store/ducks/toast/types';
import { getTaggingInformationScreen } from '../../../../../../../utils/Tagging';
import { sendTagging } from '../../../../../../../store/ducks/tagging/actions';
import { projectPavementsNeedRange } from '../../../../../../../utils/fabricationHelper';

interface IEditProjectProps {
  contractNumber: string;
  project: IFabricationProject;
  onCancel: () => void;
  updateProjectsTable: () => void;
}

const EditProject: React.FC<IEditProjectProps> = ({
  contractNumber,
  project,
  onCancel,
  updateProjectsTable
}) => {
  const dispatch = useDispatch();

  const { screenName = '', itemDescription = '' } = getTaggingInformationScreen(
    'modal-editar-projeto'
  );

  useEffect(() => {
    dispatch(
      sendTagging({
        page: window.location.pathname,
        screenName,
        event: 'page_view'
      })
    );
  }, []);

  const mockupState = useSelector(
    (appState: ApplicationState) => appState.fabricationMockups
  );
  const [isLoading, setIsloading] = useState(false);
  const [canShowCancelUploadFilesModal, setCanShowCancelUploadFilesModal] =
    useState(false);

  const handleLoadMockups = () => {
    dispatch(loadMockups(contractNumber));
  };

  useEffect(() => {
    if (
      mockupState.contractNumber !== contractNumber &&
      !mockupState.loading &&
      !mockupState.error
    ) {
      handleLoadMockups();
    }
  });

  useEffect(() => {
    if (project.hasOrders) {
      dispatch(
        addToast({
          message:
            'Não é possível editar projetos que já estejam associados a algum pedido.',
          type: EToastTypes.WARNING,
          durationInMS: 6000
        })
      );
    }
  }, []);

  const onCloseModal = () => {
    onCancel();
  };

  const getInitialProjectForm = (): IEditProjectForm => {
    const { mockupId, pavements, revision, structures } = project;
    return {
      mockupId,
      pavements: pavements
        .sort((p1, p2) => p1.position - p2.position)
        .map((pv) => Number(pv.id)),
      revision,
      structures: structures.map((st) => st.name)
    };
  };

  const [projectForm, setProjectForm] = useState<IEditProjectForm>(
    getInitialProjectForm
  );

  const canSaveProject = (): boolean => {
    if (project.hasOrders) {
      return false;
    }

    const { pavements, revision, structures } = project;
    const {
      structures: formStructures,
      pavements: formPavements,
      revision: formRevision
    } = projectForm;

    const isPavementRangeNeeded = projectPavementsNeedRange(
      formStructures || []
    );
    const isPavementsFilled =
      !!formPavements &&
      (isPavementRangeNeeded
        ? formPavements.length > 1
        : formPavements.length > 0);

    const isPavementsChanged =
      !!formPavements &&
      (formPavements.length !== pavements.length ||
        formPavements?.some((fp) => !pavements.find((p) => p.id === fp)));

    const isStructuresFilled = !!formStructures && formStructures.length > 0;

    const isStructuresChanged =
      !!formStructures &&
      formStructures.some((fs) => !structures.find((s) => s.name === fs));

    const isRevisionFilled = !!formRevision && formRevision !== '';

    const isRevisionChanged = formRevision !== revision;

    return (
      isStructuresFilled &&
      isPavementsFilled &&
      isRevisionFilled &&
      (isStructuresChanged || isPavementsChanged || isRevisionChanged)
    );
  };

  const onSaveProject = () => {
    const { structures, pavements, revision } = projectForm;
    setIsloading(true);

    FabricationProjectsService.updateProject(
      contractNumber,
      project.id,
      structures || [],
      revision || '',
      pavements
    )
      .then(() => {
        setIsloading(false);
        updateProjectsTable();
        dispatch(
          addToast({
            message: `Projeto ${project.fileName} editado com sucesso.`,
            type: EToastTypes.SUCCESS,
            durationInMS: 4000
          })
        );
        onCancel();
      })
      .catch((err) => {
        console.error(err);
        dispatch(
          addToast({
            message: `Não foi possível editar o projeto ${project.fileName}. Tente novamente.`,
            type: EToastTypes.DANGER,
            durationInMS: 6000
          })
        );
        setIsloading(false);
      });
  };

  const onCancelEditClick = () => {
    dispatch(
      sendTagging({
        page: window.location.pathname,
        screenName,
        itemDescription: `${itemDescription}cancelar`,
        event: 'click'
      })
    );

    if (project.hasOrders) {
      onCloseModal();
    } else {
      setCanShowCancelUploadFilesModal(true);
    }
  };

  return (
    <>
      <Modal
        title="Editar configurações do projeto"
        onClose={onCancelEditClick}
        buttons={{
          left: {
            children: 'Cancelar',
            name: 'cancel',
            onClick: onCancelEditClick,
            type: 'button',
            disabled: isLoading
          },
          right: {
            children: 'Enviar projeto',
            name: 'edit-project',
            onClick: () => {
              dispatch(
                sendTagging({
                  page: window.location.pathname,
                  screenName,
                  itemDescription: `${itemDescription}enviarProjeto`,
                  event: 'click'
                })
              );
              onSaveProject();
            },
            type: 'button',
            disabled: isLoading || mockupState.loading || !canSaveProject(),
            loading: isLoading
          }
        }}
      >
        <EditProjectContainer>
          <SendProjectHeader>
            <span>
              Para facilitar a gestão, insira informações sobre seus arquivos
            </span>
          </SendProjectHeader>

          <EditProjectForm
            projectForm={projectForm}
            updateProjectform={setProjectForm}
            mockupState={mockupState}
            canUpdateFields={
              !isLoading && !mockupState.loading && !project.hasOrders
            }
          />
        </EditProjectContainer>
      </Modal>

      {canShowCancelUploadFilesModal && (
        <CancelUploadFiles
          onCancelClick={onCloseModal}
          onContinueClick={() => setCanShowCancelUploadFilesModal(false)}
        />
      )}
    </>
  );
};

export default EditProject;
